import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "../components/Header";
import { useAuthDialog } from "../components/AuthDialog";
import { Markdown } from "../components/Markdown";
import { ShareButton } from "../components/ShareButton";
import { TagPill } from "../components/TagPill";
import {
  createComment,
  getReactions,
  getSession,
  listComments,
  setReaction
} from "../lib/api";
import { getPostBySlug, getReadingMinutes } from "../lib/posts";

export function PostPage() {
  const { slug } = useParams({ from: "/post/$slug" });
  const queryClient = useQueryClient();
  const { openLogin } = useAuthDialog();
  const [comment, setComment] = useState("");
  const [message, setMessage] = useState("");
  const postQuery = useQuery({
    queryKey: ["post", slug],
    queryFn: () => getPostBySlug(slug)
  });
  const post = postQuery.data;
  const sessionQuery = useQuery({ queryKey: ["session"], queryFn: getSession });
  const session = sessionQuery.data;
  const commentsQuery = useQuery({
    queryKey: ["comments", post?.id],
    queryFn: () => listComments(post!.id),
    enabled: Boolean(post?.id)
  });
  const reactionsQuery = useQuery({
    queryKey: ["reactions", post?.id, session?.id ?? "guest"],
    queryFn: () => getReactions(post!.id),
    enabled: Boolean(post?.id)
  });
  const commentMutation = useMutation({
    mutationFn: () => createComment(post!.id, comment),
    onSuccess: () => {
      setComment("");
      setMessage("Comment posted.");
      queryClient.invalidateQueries({ queryKey: ["comments", post?.id] });
      queryClient.invalidateQueries({ queryKey: ["profile-activity"] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Unable to post comment.")
  });
  const reactionMutation = useMutation({
    mutationFn: (reaction: "like" | "dislike") => setReaction(post!.id, reaction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reactions", post?.id] });
      queryClient.invalidateQueries({ queryKey: ["profile-activity"] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Unable to save reaction.")
  });

  function reactToPost(reaction: "like" | "dislike") {
    if (!session) {
      openLogin();
      return;
    }
    setMessage("");
    reactionMutation.mutate(reaction);
  }

  return (
    <>
      <Header />
      <main className="article-shell">
        <Link className="back-link" to="/blog">
          ← Back to blog
        </Link>

        {postQuery.isLoading ? <p className="muted">Loading article…</p> : null}

        {!post && !postQuery.isLoading ? (
          <section className="empty-state">
            <h1>Article not found</h1>
            <p>This article may be a draft or may not exist.</p>
          </section>
        ) : null}

        {post ? (
          <article className="article">
            <div className="post-tags">
              {(post.tags ?? []).map((tag) => (
                <TagPill key={tag.slug} tag={tag} />
              ))}
            </div>
            <h1>{post.title}</h1>
            <p className="article-summary">{post.summary}</p>
            <small>
              {post.published_at
                ? new Intl.DateTimeFormat("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                  }).format(new Date(post.published_at))
                : "Draft"}{" "}
              · {getReadingMinutes(post.content)} min read
            </small>
            {post.cover_image_url ? <img className="article-cover" src={post.cover_image_url} alt="" /> : null}
            <Markdown content={post.content} />
            <div className="resource-links">
              {post.related_project ? (
                <Link
                  className="resource-related"
                  params={{ slug: post.related_project.slug }}
                  to="/projects/$slug"
                >
                  See the project →
                </Link>
              ) : null}
              {post.github_url ? (
                <a href={post.github_url} rel="noreferrer" target="_blank">
                  GitHub
                </a>
              ) : null}
              {post.demo_url ? (
                <a href={post.demo_url} rel="noreferrer" target="_blank">
                  Live demo
                </a>
              ) : null}
            </div>
            <section className="engagement">
              <div className="reaction-box">
                <button
                  disabled={reactionMutation.isPending}
                  aria-pressed={reactionsQuery.data?.mine === "like"}
                  onClick={() => reactToPost("like")}
                  type="button"
                >
                  Like {reactionsQuery.data?.like ?? 0}
                </button>
                <button
                  disabled={reactionMutation.isPending}
                  aria-pressed={reactionsQuery.data?.mine === "dislike"}
                  onClick={() => reactToPost("dislike")}
                  type="button"
                >
                  Dislike {reactionsQuery.data?.dislike ?? 0}
                </button>
                <ShareButton
                  url={`${window.location.origin}/post/${post.slug}`}
                  title={post.title}
                  summary={post.summary}
                />
                {reactionsQuery.data?.mine ? <span>Your reaction: {reactionsQuery.data.mine === "like" ? "like" : "dislike"}</span> : null}
              </div>

              {!session ? (
                <section className="comment-login">
                  <h2>Sign in to comment</h2>
                  <p>Join the conversation with your GitHub or LinkedIn account.</p>
                  <button type="button" onClick={openLogin}>Sign in to participate</button>
                </section>
              ) : (
                <form
                  className="comment-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setMessage("");
                    commentMutation.mutate();
                  }}
                >
                  <h2>Comments</h2>
                  <p className="muted">Signed in as {session.email}</p>
                  <label htmlFor="post-comment">Your comment</label>
                  <textarea
                    id="post-comment"
                    maxLength={2000}
                    required
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="Write a comment..."
                    rows={4}
                    value={comment}
                  />
                  <button disabled={commentMutation.isPending || !comment.trim()} type="submit">
                    Post comment
                  </button>
                </form>
              )}

              {message ? <p className="status-message" role="status">{message}</p> : null}

              <div className="comment-list">
                {(commentsQuery.data ?? []).map((item) => (
                  <article key={item.id}>
                    <strong>{item.profile?.display_name ?? "Reader"}</strong>
                    <p>{item.body}</p>
                    <small>
                      {new Intl.DateTimeFormat("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric"
                      }).format(new Date(item.created_at))}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          </article>
        ) : null}
      </main>
    </>
  );
}
